/**
 * Where the copy's calls to action actually go.
 *
 * Every CTA label in Halil's document is written; only some of them have a
 * destination that exists. Naming them here rather than inlining hrefs means the
 * ones that are still pending are visible in one place, and become one edit each
 * when they land.
 */

/**
 * Destinations that exist today — imported legacy pages or the new written ones.
 *
 * **This is for CTAs used by MORE THAN ONE page.** A page's own URLs — its
 * address, its own PDF — belong in that page's module as local constants, not
 * here. Every e-book so far put two keys in this object and then read them from
 * exactly one file, which bought nothing and cost a shared-file edit: two
 * concurrent AI-editor runs adding two e-books wrote the same lines here and
 * the second could not rebase. Seven agent runs were thrown away that way on
 * 2026-08-28.
 *
 * The entries below stay as they are — moving them would rewrite the pages that
 * read them for no gain. New pages do not add to this.
 */
export const LINKS = {
  contact: '/en/contact-us',
  successStories: '/en/customers',
  hallOfFame: '/en/testimonials',
  reports: '/en/impact-labs/reports',
  /** The written solutions overview — the three offers on one page. */
  solutions: '/en/solutions',
  daimi: '/en/solutions/digital-ai-maturity-index-daimi',
  /** The Digital & AI Maturity Report 2026's own landing page (`pages/Daimi.tsx`). */
  daimiReport: '/en/impact-labs/reports/digital-ai-maturity-report-2026',
  aimi: '/en/solutions/ai-maturity-index-aimi',
  careers: '/en/become-a-digitopian',
  strategy: '/en/solutions/strategy-consulting',
  transformation: '/en/solutions/transformation-management',
  enterpriseAi: '/en/solutions/enterprise-ai-solutions',
  about: '/en/about',
  quote: '/en/request-for-quotation',

  /** The Transform Better e-book: its page here, and the file itself. */
  transformBetterEbook: '/en/impact-labs/ebooks/transform-better-e-book',
  /**
   * The PDF, in the `website-documents` bucket rather than in `public/` — it is
   * a 20 MB document that changes on marketing's schedule, not the site's, and
   * putting it in the repository would put every future revision in the git
   * history forever. Every Download button on the e-book page points here.
   */
  transformBetterEbookPdf:
    'https://njdxtkezxgkrmwwhzcne.supabase.co/storage/v1/object/public/website-documents/ebook/9ef71be7-a3d2-4a47-a3de-40ff5bbb9b7d-Transform%20Better%20Ebook%20Digitopia.pdf',

  /**
   * The Agentic AI e-book: its page here, and the file itself.
   *
   * The PDF is the same object `content/ebooks.ts` already lists for the
   * `agentic-ai` card, spelled out here because the page needs it on four
   * buttons and that file's `pdf()` helper is private to the catalogue. Same
   * bucket and the same reasoning as Transform Better's above; the `%20`s are
   * the spaces the object key genuinely carries.
   */
  agenticAiEbook: '/en/impact-labs/ebooks/agentic-ai',
  agenticAiEbookPdf:
    'https://njdxtkezxgkrmwwhzcne.supabase.co/storage/v1/object/public/website-documents/ebook/375b51e4-7359-49da-b73f-4e5bbc8d3b88-Agentic%20AI%20-%20A%20Practical%20Guide.pdf',

  /**
   * The Sustainability Innovation e-book: its page here, and the file itself.
   *
   * Same arrangement as Agentic AI's above — the PDF is the object
   * `content/ebooks.ts` already lists for that card, spelled out here because
   * the page needs it on four buttons and the catalogue's `pdf()` helper is
   * private to that file. The path is `sustainability-innovation`, which is
   * digitopia.co's own and **not** the slug the catalogue used to derive from
   * the title; the reference screenshots came from it.
   */
  sustainabilityInnovationEbook: '/en/impact-labs/ebooks/sustainability-innovation',
  sustainabilityInnovationEbookPdf:
    'https://njdxtkezxgkrmwwhzcne.supabase.co/storage/v1/object/public/website-documents/ebook/56756e66-41c8-4a3b-ba4a-12eeec5604ec-Leading%20the%20Charge%20in%20Sustainability%20Innovation.pdf',

  /**
   * The measurement e-book: its page here, and the file itself.
   *
   * Same arrangement as the three above — the PDF is the object
   * `content/ebooks.ts` already lists for that card, spelled out here because
   * the page needs it on four buttons and the catalogue's `pdf()` helper is
   * private to that file. The path is
   * `importance-of-measurement-for-transformation-success`, which is
   * digitopia.co's own and **not** the `whats-measured-gets-done` the catalogue
   * used to derive from the title; the reference screenshots came from it.
   */
  measurementEbook: '/en/impact-labs/ebooks/importance-of-measurement-for-transformation-success',
  measurementEbookPdf:
    'https://njdxtkezxgkrmwwhzcne.supabase.co/storage/v1/object/public/website-documents/ebook/e008f84d-7780-46b6-993a-ec3322b83776-The%20Importance%20of%20Measurement%20for%20Transformation%20Success.pdf',

  /**
   * The fashion-retail shopping-season e-book: its page here, and the file itself.
   *
   * Same arrangement as the four above. The path is `ai-in-fashion-retail`,
   * which is digitopia.co's own and **not** the
   * `how-fashion-leaders-are-preparing-the-shopping-season` the catalogue
   * derived from the title — the reference screenshots were captured from it,
   * the third time the title rule has been caught out.
   */
  fashionRetailEbook: '/en/impact-labs/ebooks/ai-in-fashion-retail',
  fashionRetailEbookPdf:
    'https://njdxtkezxgkrmwwhzcne.supabase.co/storage/v1/object/public/website-documents/ebook/da9e0c59-0acb-43f3-a26f-f0e87836aac3-Lead%20the%20Shopping%20Season%20with%20AI%20in%20Fashion%20Retail.pdf',

  /**
   * The Leader's Guide to Generative AI e-book: its page here, and the file itself.
   *
   * Same arrangement as the five above. The path is
   * `leaders-guide-to-generative-ai`, which is digitopia.co's own and **not** the
   * `the-leaders-guide-to-generative-ai` the catalogue derived from the title —
   * the reference screenshots were captured from it (the filenames carry the
   * address), the fourth time the title rule has been caught out.
   */
  generativeAiEbook: '/en/impact-labs/ebooks/leaders-guide-to-generative-ai',
  generativeAiEbookPdf:
    'https://njdxtkezxgkrmwwhzcne.supabase.co/storage/v1/object/public/website-documents/ebook/d64f10d2-f3d8-462f-b24e-dff774ddb251-The%20Leader_s%20Guide%20to%20Generative%20AI.pdf',

  /**
   * The two assessment tools the copy sends people to. They lived in a `PENDING`
   * table until 2026-08-26 — Kagan had built both as standalone HTML and neither
   * had a page here, so both buttons opened the contact form instead. Both are
   * ported now (`/tools`), so they are ordinary destinations like the rest.
   *
   * Named for what the *copy* calls them rather than for their page titles, so
   * the fourteen call sites did not have to change when the pages arrived — and
   * will not have to if a tool is ever renamed again.
   */
  readinessTool: '/en/tools/agentic-readiness-index',
  appetiteTool: '/en/tools/transformation-readiness',

  /**
   * The third tool. Added when the home page stopped describing the readiness
   * quadrant and started signposting all three tools by name — Kagan,
   * 2026-08-26: *"tool lara net ve anlaşılır
   * şekilde yönlendirecek şekilde yeniden tasarlayalım."*
   */
  gutsCheckTool: '/en/tools/agentic-guts-check',
}

